import { MODES } from "#src/constants/mode.ts";
import { type Product, type ProductTypeInfo } from "#src/types/Product.ts";
import { PRODUCTS } from "#src/constants/products.ts";

import {
  SOLVE,
  insuredQuoteData,
  type PayDurationDataItem,
  type QuoteParams,
  type QuoteRequestParams,
  type SectionedSolves,
  type SolveDataItem,
  type RenderRules,
  type ModeDataItem,
} from "#src/types/QuoteParams.ts";

export interface WholeLifeProductTypeInfo extends ProductTypeInfo {
  isPermanent: boolean;
  solves: SectionedSolves;
  allSolves: SolveDataItem[];
  payDurationOptions: PayDurationDataItem[];
}

function showFaceAmount(args: QuoteParams) {
  return args.solve === SOLVE.BASE_PREMIUM;
}

function showPremium(args: QuoteParams) {
  return args.solve === SOLVE.BASE_DEATH_BENEFIT;
}

function showHealth(conversion: boolean) {
  return !conversion;
}

const solves = {
  premium_solves: [
    {
      value: SOLVE.BASE_PREMIUM,
      text: "Premium",
    },
  ],
  death_benefit_solves: [
    {
      value: SOLVE.BASE_DEATH_BENEFIT,
      text: "Death Benefit",
    },
  ],
};

export default function WholeLife(): WholeLifeProductTypeInfo {
  return {
    get display(): string {
      return "Whole Life";
    },
    get type(): Product {
      return PRODUCTS.WHOLE_LIFE;
    },
    get isPermanent(): boolean {
      return true;
    },
    get defaults(): Partial<QuoteRequestParams> {
      return {
        pay_duration: -1,
        solve: SOLVE.BASE_PREMIUM,
        categories: [this.requestCategory],
        save_age: false,
      };
    },
    get solves(): SectionedSolves {
      return solves;
    },
    get allSolves(): SolveDataItem[] {
      const all: SolveDataItem[] = [];
      for (const solveType of Object.values(solves)) {
        all.push(...solveType);
      }
      return all;
    },
    get requestCategory(): string {
      return "whole_life.none";
    },
    get modes(): ModeDataItem[] {
      return MODES;
    },
    renderRules(params, options = { conversion: false }): Partial<RenderRules> {
      const faceAmount = showFaceAmount(params);
      const premium = showPremium(params);

      return {
        saveAge: true,
        faceAmount: faceAmount,
        payDuration: true,
        premium: premium,
        solve: true,
        insuredHealth: showHealth(options.conversion),
        mode: true,
        state: true,
      };
    },
    get payDurationOptions(): PayDurationDataItem[] {
      return [
        {
          value: -1,
          title: "Paid up at age 100",
        },
        {
          value: 20,
          title: "20 Pay",
        },
        {
          value: 15,
          title: "15 Pay",
        },
        {
          value: 10,
          title: "10 Pay",
        },
        {
          value: 1,
          title: "Single Pay",
        },
      ];
    },
    eAppParser(model): Partial<QuoteParams> {
      let payDuration = model?.pay_duration;
      if (!this.payDurationOptions.some(v => v.value === payDuration)) {
        payDuration = this.defaults.pay_duration;
      }

      let solve = this.defaults.solve;
      if (this.allSolves.some(v => v.value === model?.solve)) {
        solve = model.solve;
      }

      let saveAge = model.save_age;
      if (!(typeof saveAge === "boolean")) {
        saveAge = this.defaults.save_age;
      }

      //NOTE(tim) this is the only occurrence of something funky like this
      let face_amount = model?.face_amount || model?.initial_death_benefit;
      if (!face_amount) face_amount = 1000000;

      return {
        pay_duration: payDuration,
        premium: model?.premium,
        solve,
        face_amount,
        save_age: saveAge,
      };
    },
    toQuoteRequest(args): Partial<QuoteRequestParams> {
      const body: Partial<QuoteRequestParams> = {
        agent: { id: args.agentId },
        compare: false,

        ...insuredQuoteData(args.insured),

        selected_type: args.params.selected_type,
        state: args.params.state,
        categories: this.defaults.categories,

        mode: args.params.mode,
        pay_duration: args.params.pay_duration,
        solve: args.params.solve,
        save_age: args.params.save_age,
      };

      const renderRules = this.renderRules(args.params, {
        conversion: Boolean(args.conversion),
      });

      if (renderRules.faceAmount) {
        body.face_amount = args.params.face_amount;
      }
      if (renderRules.premium) {
        body.premium = args.params.premium;
      }

      return body;
    },
  };
}
